/**
 * @fileoverview Add Paint Selection Modal Component
 *
 * Modal form for clients to add new paint/stain selections to their project.
 * Integrates with Firebase functions to create records in Airtable.
 *
 * Features:
 * - Paint vs Stain toggle (Stain skips brand selection)
 * - Sherwin-Williams color picker with swatches
 * - Other brand text inputs
 * - Dynamic sheen/texture defaults based on area selection
 * - Conditional upgrade confirmation checkbox
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ../hooks/useSelections
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  FaTimes,
  FaPlus,
  FaCheck,
  FaSearch,
  FaExclamationTriangle,
  FaPalette,
} from "react-icons/fa";
import {
  useAddPaintSelection,
  useUpdatePaintSelection,
  useSWPaintColors,
} from "../hooks/useSelections";
import "./SelectionModals.css";

/**
 * Paint or Stain type options
 * @constant {Array<Object>}
 */
const PAINT_STAIN_OPTIONS = [
  { value: "Paint", label: "Paint" },
  { value: "Stain", label: "Stain" },
];

/**
 * Brand options (only shown for Paint)
 * @constant {Array<Object>}
 */
const BRAND_OPTIONS = [
  { value: "Sherwin-Williams", label: "Sherwin-Williams" },
  { value: "Other", label: "Other" },
];

/**
 * Area options for multi-select
 * @constant {Array<Object>}
 */
const AREA_OPTIONS = [
  {
    value: "Interior - Primary Color (walls)",
    label: "Interior - Primary Color (walls)",
  },
  { value: "Interior - Doors", label: "Interior - Doors" },
  { value: "Interior - Trim", label: "Interior - Trim" },
  { value: "Interior - Ceiling", label: "Interior - Ceiling" },
  { value: "Interior - Accent 1", label: "Interior - Accent 1" },
  { value: "Interior - Accent 2", label: "Interior - Accent 2" },
  {
    value: "Exterior - Primary Color (siding)",
    label: "Exterior - Primary Color (siding)",
  },
  { value: "Exterior - Trim / Shutters", label: "Exterior - Trim / Shutters" },
  { value: "Exterior - Front Door", label: "Exterior - Front Door" },
  { value: "Exterior - Other Doors", label: "Exterior - Other Doors" },
  { value: "Exterior - Accent Color", label: "Exterior - Accent Color" },
  { value: "Mantle", label: "Mantle" },
  { value: "Other", label: "Other" },
];

/**
 * Trim areas that have different default sheen
 * @constant {string[]}
 */
const TRIM_AREAS = ["Interior - Trim", "Exterior - Trim / Shutters"];

/**
 * Sheen options
 * @constant {Array<Object>}
 */
const SHEEN_OPTIONS = [
  { value: "Flat/Matte", label: "Flat/Matte" },
  { value: "Eggshell", label: "Eggshell" },
  { value: "Satin", label: "Satin" },
  { value: "Semi-Gloss", label: "Semi-Gloss" },
  { value: "Gloss", label: "Gloss" },
  { value: "High Gloss", label: "High Gloss" },
  { value: "N/A", label: "N/A" },
];

/**
 * Texture options
 * @constant {Array<Object>}
 */
const TEXTURE_OPTIONS = [
  { value: "Smooth", label: "Smooth" },
  { value: "Knockdown", label: "Knockdown" },
  { value: "Orange Peel", label: "Orange Peel" },
  { value: "Skip Trowel", label: "Skip Trowel" },
  { value: "Sand Swirl", label: "Sand Swirl" },
  { value: "Stomp Brush", label: "Stomp Brush" },
  { value: "N/A", label: "N/A" },
];

/**
 * Standard (non-upgrade) sheens for non-trim areas
 * @constant {string[]}
 */
const STANDARD_SHEENS = ["Flat/Matte", "Eggshell", "N/A"];

/**
 * Standard (non-upgrade) textures
 * @constant {string[]}
 */
const STANDARD_TEXTURES = ["Smooth", "N/A"];

/**
 * Sheens that trigger upgrade for trim-only selections
 * @constant {string[]}
 */
const TRIM_UPGRADE_SHEENS = ["Gloss", "High Gloss"];

/**
 * Normalize a hex color value for use as a CSS color.
 *
 * The SW Paint Colors data stores hex values without a leading "#"
 * (e.g. "E8E3D9"), which is invalid CSS and renders as a blank swatch.
 * This ensures a "#" prefix while leaving valid/empty values alone.
 *
 * @param {string} hex - Raw hex value (with or without "#")
 * @returns {string} CSS-ready hex color, or "#ccc" fallback
 */
const normalizeHex = (hex) => {
  if (!hex) return "#ccc";
  return hex.startsWith("#") ? hex : `#${hex}`;
};

/**
 * AddPaintModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Callback to close modal
 * @param {string} props.projectId - Airtable project record ID
 * @param {"add"|"edit"} [props.mode="add"] - Whether the modal creates
 *   a new selection or edits an existing one
 * @param {Object|null} [props.initialData=null] - Existing paint row to
 *   edit (as mapped by getPaintSelections). Required when mode is "edit"
 * @returns {JSX.Element|null} Modal component or null if closed
 */
const AddPaintModal = ({
  isOpen,
  onClose,
  projectId,
  mode = "add",
  initialData = null,
}) => {
  const isEdit = mode === "edit";

  // Form state
  const [formData, setFormData] = useState({
    paintStainType: "",
    brand: "",
    swPaintColorId: "",
    otherBrand: "",
    paintCode: "",
    areas: [],
    sheen: "Flat/Matte",
    texture: "Smooth",
    locationNotes: "",
    isUpgrade: false,
  });

  // SW Color picker state
  const [colorSearch, setColorSearch] = useState("");
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  const colorPickerRef = useRef(null);

  // While true, the area/paintStainType auto-default effects are
  // suppressed so hydrating an existing record in edit mode doesn't
  // clobber its stored sheen/texture/brand. Cleared on the next tick.
  const hydratingRef = useRef(false);

  // UI state
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  // Mutation hooks
  const addPaintMutation = useAddPaintSelection();
  const updatePaintMutation = useUpdatePaintSelection();
  const activeMutation = isEdit ? updatePaintMutation : addPaintMutation;
  const { data: swPaintColors = [], isLoading: colorsLoading } =
    useSWPaintColors();

  /**
   * Lock body scroll when modal is open
   */
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [isOpen]);

  /**
   * Reset (add mode) or hydrate from the existing record (edit mode)
   * when the modal opens.
   */
  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && initialData) {
      // Derive paint/stain type and brand from the stored row:
      // an SW link => Sherwin-Williams paint; a stain is stored with
      // sheen/texture "N/A"; anything else is an "Other" brand paint.
      const hasSwLink = Boolean(initialData.swPaintSelection);
      const isStain =
        !hasSwLink &&
        initialData.sheen === "N/A" &&
        initialData.texture === "N/A";

      const paintStainType = isStain ? "Stain" : "Paint";
      const brand = hasSwLink ? "Sherwin-Williams" : "Other";

      hydratingRef.current = true;
      setFormData({
        paintStainType,
        brand,
        swPaintColorId: hasSwLink ? initialData.swPaintSelection : "",
        otherBrand: initialData.otherBrand || "",
        paintCode: initialData.paintCode || "",
        areas: Array.isArray(initialData.areas) ? [...initialData.areas] : [],
        sheen: initialData.sheen || "Flat/Matte",
        texture: initialData.texture || "Smooth",
        locationNotes: initialData.locationNotes || "",
        isUpgrade: Boolean(initialData.isUpgrade),
      });
      // Release the guard after the state-driven effects have run.
      const timer = setTimeout(() => {
        hydratingRef.current = false;
      }, 0);
      setColorSearch("");
      setShowColorDropdown(false);
      setErrors({});
      setShowSuccess(false);
      return () => clearTimeout(timer);
    }

    setFormData({
      paintStainType: "",
      brand: "",
      swPaintColorId: "",
      otherBrand: "",
      paintCode: "",
      areas: [],
      sheen: "Flat/Matte",
      texture: "Smooth",
      locationNotes: "",
      isUpgrade: false,
    });
    setColorSearch("");
    setShowColorDropdown(false);
    setErrors({});
    setShowSuccess(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * Close color dropdown when clicking outside the picker
   */
  useEffect(() => {
    if (!showColorDropdown) return;

    const handleClickOutside = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowColorDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColorDropdown]);

  /**
   * Determine if only trim areas are selected
   */
  const hasOnlyTrimAreas = useMemo(() => {
    if (formData.areas.length === 0) return false;
    return formData.areas.every((area) => TRIM_AREAS.includes(area));
  }, [formData.areas]);

  /**
   * Determine if any non-trim areas are selected
   */
  const hasNonTrimAreas = useMemo(() => {
    return formData.areas.some((area) => !TRIM_AREAS.includes(area));
  }, [formData.areas]);

  /**
   * Calculate if upgrade checkbox should show
   */
  const showUpgradeCheckbox = useMemo(() => {
    if (formData.areas.length === 0) return false;

    const isNonTrimSheenUpgrade =
      hasNonTrimAreas && !STANDARD_SHEENS.includes(formData.sheen);

    const isNonTrimTextureUpgrade =
      hasNonTrimAreas && !STANDARD_TEXTURES.includes(formData.texture);

    const isTrimUpgrade =
      hasOnlyTrimAreas && TRIM_UPGRADE_SHEENS.includes(formData.sheen);

    return isNonTrimSheenUpgrade || isNonTrimTextureUpgrade || isTrimUpgrade;
  }, [
    formData.areas,
    formData.sheen,
    formData.texture,
    hasNonTrimAreas,
    hasOnlyTrimAreas,
  ]);

  /**
   * Get upgrade details for helper text
   */
  const upgradeDetails = useMemo(() => {
    const details = [];

    if (hasOnlyTrimAreas && TRIM_UPGRADE_SHEENS.includes(formData.sheen)) {
      details.push(formData.sheen);
    } else if (hasNonTrimAreas && !STANDARD_SHEENS.includes(formData.sheen)) {
      details.push(formData.sheen);
    }

    if (!STANDARD_TEXTURES.includes(formData.texture)) {
      details.push(formData.texture);
    }

    return details.join(", ");
  }, [formData.sheen, formData.texture, hasOnlyTrimAreas, hasNonTrimAreas]);

  /**
   * Update defaults when areas change
   */
  useEffect(() => {
    if (hydratingRef.current) return;
    if (formData.areas.length === 0) return;

    const onlyTrim = formData.areas.every((area) => TRIM_AREAS.includes(area));

    if (onlyTrim) {
      setFormData((prev) => ({
        ...prev,
        sheen: "Semi-Gloss",
        texture: "Smooth",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        sheen: "Flat/Matte",
        texture: "Smooth",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.areas.length, formData.areas.join(",")]);

  /**
   * Reset upgrade checkbox when it should hide
   */
  useEffect(() => {
    if (!showUpgradeCheckbox) {
      setFormData((prev) => ({ ...prev, isUpgrade: false }));
    }
  }, [showUpgradeCheckbox]);

  /**
   * Reset brand when switching to Stain
   */
  useEffect(() => {
    if (hydratingRef.current) return;
    if (formData.paintStainType === "Stain") {
      setFormData((prev) => ({
        ...prev,
        brand: "Other",
        swPaintColorId: "",
        sheen: "N/A",
        texture: "N/A",
      }));
      setColorSearch("");
      setShowColorDropdown(false);
    } else if (formData.paintStainType === "Paint") {
      // Reset to paint defaults based on area selection
      const onlyTrim =
        formData.areas.length > 0 &&
        formData.areas.every((area) => TRIM_AREAS.includes(area));

      setFormData((prev) => ({
        ...prev,
        brand: "",
        sheen: onlyTrim ? "Semi-Gloss" : "Flat/Matte",
        texture: "Smooth",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.paintStainType]);

  /**
   * Filter SW colors based on search
   */
  const filteredColors = useMemo(() => {
    if (!colorSearch.trim()) return [];

    const search = colorSearch.toLowerCase();
    return swPaintColors
      .filter(
        (color) =>
          color.colorName?.toLowerCase().includes(search) ||
          color.colorNumber?.toLowerCase().includes(search) ||
          color.paintColor?.toLowerCase().includes(search),
      )
      .slice(0, 50);
  }, [swPaintColors, colorSearch]);

  /**
   * Get selected SW color details
   */
  const selectedColor = useMemo(() => {
    if (!formData.swPaintColorId) return null;
    return swPaintColors.find((c) => c.id === formData.swPaintColorId);
  }, [formData.swPaintColorId, swPaintColors]);

  /**
   * Handle input field changes
   * @param {Event} e - Change event
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  /**
   * Handle area checkbox toggle
   * @param {string} area - Area value to toggle
   */
  const handleAreaToggle = (area) => {
    setFormData((prev) => ({
      ...prev,
      areas: prev.areas.includes(area)
        ? prev.areas.filter((a) => a !== area)
        : [...prev.areas, area],
    }));

    if (errors.areas) {
      setErrors((prev) => ({
        ...prev,
        areas: null,
      }));
    }
  };

  /**
   * Handle SW color selection
   * @param {Object} color - Selected color object
   */
  const handleColorSelect = (color) => {
    setFormData((prev) => ({
      ...prev,
      swPaintColorId: color.id,
    }));
    setColorSearch("");
    setShowColorDropdown(false);

    if (errors.swPaintColorId) {
      setErrors((prev) => ({
        ...prev,
        swPaintColorId: null,
      }));
    }
  };

  /**
   * Clear selected SW color
   */
  const handleClearColor = () => {
    setFormData((prev) => ({
      ...prev,
      swPaintColorId: "",
    }));
  };

  /**
   * Validate form data
   * @returns {boolean} Whether form is valid
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.paintStainType) {
      newErrors.paintStainType = "Please select Paint or Stain";
    }

    // Brand validation only for Paint
    if (formData.paintStainType === "Paint" && !formData.brand) {
      newErrors.brand = "Please select a brand";
    }

    // SW Color validation
    if (formData.brand === "Sherwin-Williams" && !formData.swPaintColorId) {
      newErrors.swPaintColorId = "Please select a color";
    }

    // Other brand validation (for Paint with Other brand, or Stain)
    if (formData.brand === "Other" || formData.paintStainType === "Stain") {
      if (!formData.otherBrand.trim()) {
        newErrors.otherBrand = "Brand name is required";
      }
      if (!formData.paintCode.trim()) {
        newErrors.paintCode = "Code and color name is required";
      }
    }

    if (formData.areas.length === 0) {
      newErrors.areas = "Please select at least one area";
    }

    if (!formData.sheen) {
      newErrors.sheen = "Please select a sheen";
    }

    if (formData.paintStainType === "Paint" && !formData.texture) {
      newErrors.texture = "Please select a texture";
    }

    if (showUpgradeCheckbox && !formData.isUpgrade) {
      newErrors.isUpgrade = "Please confirm your upgrade selection";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   * @param {Event} e - Submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const isSW =
      formData.brand === "Sherwin-Williams" &&
      formData.paintStainType === "Paint";

    // Human-readable name for the chosen SW color (e.g. "SW 7015
    // Repose Gray"), used to label activity log entries.
    const swColorLabel = selectedColor
      ? selectedColor.displayName ||
        [selectedColor.colorNumber, selectedColor.colorName]
          .filter(Boolean)
          .join(" ")
      : "";

    try {
      if (isEdit) {
        // Build the full field set. Explicit clear keys on the opposite
        // brand ensure a record never keeps both an SW link and free-text
        // brand/code after switching brands.
        const updates = {
          areas: formData.areas,
          sheen: formData.paintStainType === "Paint" ? formData.sheen : "N/A",
          texture:
            formData.paintStainType === "Paint" ? formData.texture : "N/A",
          locationNotes: formData.locationNotes.trim() || null,
          isUpgrade: showUpgradeCheckbox ? formData.isUpgrade : false,
        };

        if (isSW) {
          updates.swPaintColorId = formData.swPaintColorId;
          updates.otherBrand = null;
          updates.paintCode = null;
        } else {
          updates.swPaintColorId = null;
          updates.otherBrand = formData.otherBrand.trim();
          updates.paintCode = formData.paintCode.trim();
        }

        await updatePaintMutation.mutateAsync({
          paintId: initialData.id,
          projectId,
          updates,
          // Display-only label for the activity log (not written to
          // Airtable) — the SW branch clears paintCode, so the log
          // would otherwise have nothing to name the record by.
          displayLabel: isSW ? swColorLabel : formData.paintCode.trim(),
        });
      } else {
        const paintData = {
          paintStainType: formData.paintStainType,
          brand: formData.paintStainType === "Paint" ? formData.brand : "Other",
          areas: formData.areas,
          sheen: formData.paintStainType === "Paint" ? formData.sheen : "N/A",
          texture:
            formData.paintStainType === "Paint" ? formData.texture : "N/A",
          locationNotes: formData.locationNotes.trim() || null,
          isUpgrade: showUpgradeCheckbox ? formData.isUpgrade : false,
        };

        // Add brand-specific fields
        if (isSW) {
          paintData.swPaintColorId = formData.swPaintColorId;
          // Display-only label for the activity log; the SW link itself
          // is what gets written to Airtable.
          paintData.displayLabel = swColorLabel;
        } else {
          paintData.otherBrand = formData.otherBrand.trim();
          paintData.paintCode = formData.paintCode.trim();
        }

        await addPaintMutation.mutateAsync({
          projectId,
          ...paintData,
        });
      }

      setShowSuccess(true);

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error saving paint selection:", error);
      setErrors({
        submit:
          error.message || "Failed to save paint selection. Please try again.",
      });
    }
  };

  /**
   * Handle overlay click to close
   * @param {Event} e - Click event
   */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="selection-modal modal-lg">
        {/* Header */}
        <div className="selection-modal-header">
          <div className="selection-modal-header-left">
            <div className="selection-modal-header-icon">
              <FaPalette />
            </div>
            <h2 className="selection-modal-title">
              {isEdit ? "Edit Paint Selection" : "Add Paint Selection"}
            </h2>
          </div>
          <button
            className="selection-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {showSuccess ? (
          <div className="selection-modal-body">
            <div className="selection-modal-success">
              <div className="success-icon">
                <FaCheck />
              </div>
              <h3>
                {isEdit ? "Paint Selection Updated!" : "Paint Selection Added!"}
              </h3>
              <p>Your paint selection has been saved successfully.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="selection-form">
            <div className="selection-modal-body">
              <div className="selection-form-body">
                <div class="form-section">
                  <div className="form-section-title">Color Selection</div>
                  {/* Paint or Stain Toggle */}
                  <div className="form-group">
                    <label className="form-label">
                      Paint or Stain? <span className="required">*</span>
                    </label>
                    <div className="radio-group">
                      {PAINT_STAIN_OPTIONS.map((option) => (
                        <label key={option.value} className="radio-label">
                          <input
                            type="radio"
                            name="paintStainType"
                            value={option.value}
                            checked={formData.paintStainType === option.value}
                            onChange={handleChange}
                          />
                          <span className="radio-custom"></span>
                          {option.label}
                        </label>
                      ))}
                    </div>
                    {errors.paintStainType && (
                      <span className="form-error">
                        {errors.paintStainType}
                      </span>
                    )}
                  </div>

                  {/* Brand Selection - Only show for Paint */}
                  {formData.paintStainType === "Paint" && (
                    <div className="form-group">
                      <label htmlFor="brand" className="form-label">
                        Brand <span className="required">*</span>
                      </label>
                      <select
                        id="brand"
                        name="brand"
                        value={formData.brand}
                        onChange={handleChange}
                        className={`form-select ${errors.brand ? "error" : ""}`}
                      >
                        <option value="">Select brand...</option>
                        {BRAND_OPTIONS.map((brand) => (
                          <option key={brand.value} value={brand.value}>
                            {brand.label}
                          </option>
                        ))}
                      </select>
                      {errors.brand && (
                        <span className="form-error">{errors.brand}</span>
                      )}
                    </div>
                  )}

                  {/* Sherwin-Williams Color Picker */}
                  {formData.paintStainType === "Paint" &&
                    formData.brand === "Sherwin-Williams" && (
                      <div className="form-group">
                        <label className="form-label">
                          Color <span className="required">*</span>
                        </label>

                        {/* Selected Color Display */}
                        {selectedColor ? (
                          <div className="paint-color-selected">
                            <div
                              className="paint-color-swatch-large"
                              style={{
                                backgroundColor: normalizeHex(selectedColor.hex),
                              }}
                            />
                            <div className="paint-color-selected-info">
                              <span className="paint-color-name">
                                {selectedColor.colorName}
                              </span>
                              <span className="paint-color-number">
                                {selectedColor.colorNumber}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="paint-color-clear"
                              onClick={handleClearColor}
                              aria-label="Clear selection"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ) : (
                          <div className="paint-color-picker" ref={colorPickerRef}>
                            <div className="paint-color-search">
                              <FaSearch className="paint-color-search-icon" />
                              <input
                                type="text"
                                placeholder="Search colors by name or number..."
                                value={colorSearch}
                                onChange={(e) => {
                                  setColorSearch(e.target.value);
                                  setShowColorDropdown(true);
                                }}
                                onFocus={() => setShowColorDropdown(true)}
                                className={`form-input ${
                                  errors.swPaintColorId ? "error" : ""
                                }`}
                              />
                            </div>

                            {showColorDropdown && (
                              <div className="paint-color-dropdown">
                                {colorsLoading ? (
                                  <div className="paint-color-loading">
                                    Loading colors...
                                  </div>
                                ) : !colorSearch.trim() ? (
                                  <div className="paint-color-empty">
                                    Start typing to find your color
                                  </div>
                                ) : filteredColors.length === 0 ? (
                                  <div className="paint-color-empty">
                                    No colors found
                                  </div>
                                ) : (
                                  <ul className="paint-color-list">
                                    {filteredColors.map((color) => (
                                      <li
                                        key={color.id}
                                        className="paint-color-option"
                                        onClick={() => handleColorSelect(color)}
                                      >
                                        <div
                                          className="paint-color-swatch"
                                          style={{
                                            backgroundColor: normalizeHex(
                                              color.hex,
                                            ),
                                          }}
                                        />
                                        <div className="paint-color-info">
                                          <span className="paint-color-name">
                                            {color.colorName}
                                          </span>
                                          <span className="paint-color-number">
                                            {color.colorNumber}
                                          </span>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {errors.swPaintColorId && (
                          <span className="form-error">
                            {errors.swPaintColorId}
                          </span>
                        )}
                      </div>
                    )}

                  {/* Other Brand Fields - Show for Paint+Other OR Stain */}
                  {((formData.paintStainType === "Paint" &&
                    formData.brand === "Other") ||
                    formData.paintStainType === "Stain") && (
                    <>
                      <div className="form-group">
                        <label htmlFor="otherBrand" className="form-label">
                          Brand Name <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="otherBrand"
                          name="otherBrand"
                          value={formData.otherBrand}
                          onChange={handleChange}
                          placeholder={
                            formData.paintStainType === "Stain"
                              ? "e.g., Minwax, Varathane, Cabot"
                              : "e.g., Benjamin Moore, Behr"
                          }
                          className={`form-input ${
                            errors.otherBrand ? "error" : ""
                          }`}
                        />
                        {errors.otherBrand && (
                          <span className="form-error">
                            {errors.otherBrand}
                          </span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="paintCode" className="form-label">
                          {formData.paintStainType === "Stain"
                            ? "Stain Code + Color Name"
                            : "Paint Code + Color Name"}{" "}
                          <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="paintCode"
                          name="paintCode"
                          value={formData.paintCode}
                          onChange={handleChange}
                          placeholder={
                            formData.paintStainType === "Stain"
                              ? "e.g., 2716 Early American"
                              : "e.g., OC-17 White Dove"
                          }
                          className={`form-input ${
                            errors.paintCode ? "error" : ""
                          }`}
                        />
                        {errors.paintCode && (
                          <span className="form-error">{errors.paintCode}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div class="form-section">
                  <div className="form-section-title">Location Information</div>
                  {/* Areas Multi-Select */}
                  <div className="form-group">
                    <label className="form-label">
                      Area(s) <span className="required">*</span>
                    </label>
                    <p className="form-hint">
                      Select all areas where this color will be applied
                    </p>
                    <div className="checkbox-grid">
                      {AREA_OPTIONS.map((area) => (
                        <label
                          key={area.value}
                          className={`checkbox-label ${
                            formData.areas.includes(area.value)
                              ? "selected"
                              : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.areas.includes(area.value)}
                            onChange={() => handleAreaToggle(area.value)}
                          />
                          <span className="checkbox-custom"></span>
                          {area.label}
                        </label>
                      ))}
                    </div>
                    {errors.areas && (
                      <span className="form-error">{errors.areas}</span>
                    )}
                  </div>

                  {/* Location Notes */}
                  <div className="form-group">
                    <label htmlFor="locationNotes" className="form-label">
                      Location Notes
                    </label>
                    <textarea
                      id="locationNotes"
                      name="locationNotes"
                      value={formData.locationNotes}
                      onChange={handleChange}
                      placeholder="Specify 'Other' area or rooms/walls or varying sheens/textures."
                      className="form-textarea"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Sheen and Texture Row */}
                {formData.paintStainType === "Paint" && (
                  <div class="form-section">
                    <div className="form-section-title">
                      Style/Upgrade Selections
                    </div>
                    <div className="form-row">
                      <div className="form-group form-group-half">
                        <label htmlFor="sheen" className="form-label">
                          Sheen <span className="required">*</span>
                        </label>
                        <select
                          id="sheen"
                          name="sheen"
                          value={formData.sheen}
                          onChange={handleChange}
                          className={`form-select ${errors.sheen ? "error" : ""}`}
                        >
                          {SHEEN_OPTIONS.map((sheen) => (
                            <option key={sheen.value} value={sheen.value}>
                              {sheen.label}
                            </option>
                          ))}
                        </select>
                        <p className="form-hint">
                          Flat/Matte = budgeted for non-trim areas
                          <br />
                          Semi-gloss or lower = budgeted for trim
                        </p>
                        {errors.sheen && (
                          <span className="form-error">{errors.sheen}</span>
                        )}
                      </div>
                      <div className="form-group form-group-half">
                        <label htmlFor="texture" className="form-label">
                          Texture <span className="required">*</span>
                        </label>
                        <select
                          id="texture"
                          name="texture"
                          value={formData.texture}
                          onChange={handleChange}
                          className={`form-select ${errors.texture ? "error" : ""}`}
                        >
                          {TEXTURE_OPTIONS.map((texture) => (
                            <option key={texture.value} value={texture.value}>
                              {texture.label}
                            </option>
                          ))}
                        </select>
                        <p className="form-hint">
                          Smooth is budgeted. Texture will only apply to
                          interior walls and/or ceilings.
                        </p>
                        {errors.texture && (
                          <span className="form-error">{errors.texture}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {/* Upgrade Confirmation Checkbox */}
                {showUpgradeCheckbox && (
                  <div className="upgrade-banner">
                    <FaExclamationTriangle className="upgrade-banner-icon" />
                    <div className="upgrade-banner-content">
                      <label
                        className={`checkbox-label checkbox-inline ${
                          formData.isUpgrade ? "selected" : ""
                        } ${errors.isUpgrade ? "error" : ""}`}
                      >
                        <input
                          type="checkbox"
                          name="isUpgrade"
                          checked={formData.isUpgrade}
                          onChange={handleChange}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="upgrade-label">
                          Confirm Upgrade Selection{" "}
                          <span className="required">*</span>
                        </span>
                      </label>
                      <p className="upgrade-helper">
                        You selected the following sheen and/or texture, which
                        may be considered an upgrade to your initial proposal:{" "}
                        <strong>{upgradeDetails}</strong>
                      </p>
                      {errors.isUpgrade && (
                        <span className="form-error">{errors.isUpgrade}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Submit Error */}
                {errors.submit && (
                  <div className="form-error-banner">{errors.submit}</div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="selection-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={onClose}
                disabled={activeMutation.isPending}
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={activeMutation.isPending}
              >
                {activeMutation.isPending ? (
                  <>
                    <span className="spinner-small"></span>
                    {isEdit ? "Saving..." : "Adding..."}
                  </>
                ) : (
                  <>
                    {isEdit ? (
                      <>
                        <FaCheck /> Save Changes
                      </>
                    ) : (
                      <>
                        <FaPlus /> Add Paint Selection
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddPaintModal;
